class MainPdpGallery {
  constructor(el) {
    this.el = el;
    this.track = el.querySelector('[data-pdp-gallery-track]');
    this.prevBtn = el.querySelector('[data-pdp-gallery-prev]');
    this.nextBtn = el.querySelector('[data-pdp-gallery-next]');
    this.thumbsContainer = el.closest('.main-pdp__media-col')?.querySelector('[data-pdp-thumbs]') || null;

    // "all*" are the full, unfiltered element lists (stable — data-pdp-slide/data-pdp-thumb
    // index values on them stay tied to their position in product.media for other consumers,
    // e.g. boost-result-popup.js reads data-pdp-thumb directly). "slides/dots/thumbs" are the
    // currently active subset — filterByVariant() narrows these to one variant's photos, and
    // every other method (goTo, updateBtnState, ...) only ever looks at the active subset.
    this.allSlides = [...el.querySelectorAll('[data-pdp-slide]')];
    this.allDots = [...el.querySelectorAll('[data-pdp-dot]')];
    this.allThumbs = this.thumbsContainer ? [...this.thumbsContainer.querySelectorAll('[data-pdp-thumb]')] : [];

    // Liquid already decided what's visible on first paint (default product.media slides,
    // or — if the initially selected variant has custom.variant_images — only that
    // variant's slides; see sections/main-pdp.liquid). Read that instead of assuming the
    // default set, so the two stay in sync without duplicating the "which variant" logic here.
    const isVisible = (el) => !el.classList.contains('hidden');
    this.slides = this.allSlides.filter(isVisible);
    this.dots = this.allDots.filter(isVisible);
    this.thumbs = this.allThumbs.filter(isVisible);
    this.currentIndex = 0;
    this.productName = el.closest('[data-product-title]')?.dataset.productTitle || '';
    this.bindEvents();
    this.updateBtnState();
  }

  bindEvents() {
    this.prevBtn?.addEventListener('click', () => this.goTo(this.currentIndex - 1, 'left_arrow_click'));
    this.nextBtn?.addEventListener('click', () => this.goTo(this.currentIndex + 1, 'right_arrow_click'));
    this.allDots.forEach((dot) => {
      dot.addEventListener('click', () => this.goTo(this.dots.indexOf(dot), 'dot_click'));
    });
    this.allThumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => this.goTo(this.thumbs.indexOf(thumb), 'thumb_click'));
    });

    let touchStartX = 0;
    let touchDeltaX = 0;

    this.el.addEventListener('touchstart', (e) => {
      touchStartX = e.touches[0].clientX;
      touchDeltaX = 0;
    }, { passive: true });

    this.el.addEventListener('touchmove', (e) => {
      touchDeltaX = e.touches[0].clientX - touchStartX;
    }, { passive: true });

    this.el.addEventListener('touchend', () => {
      if (Math.abs(touchDeltaX) > 40) {
        const swipeSource = touchDeltaX < 0 ? 'left_swipe' : 'right_swipe';
        this.goTo(touchDeltaX < 0 ? this.currentIndex + 1 : this.currentIndex - 1, swipeSource);
      }
      touchDeltaX = 0;
    });

    // Trackpad two-finger horizontal swipe (desktop)
    let wheelLocked = false;
    this.el.addEventListener('wheel', (e) => {
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return; // ignore vertical scroll
      if (wheelLocked) return;
      wheelLocked = true;
      const wheelSource = e.deltaX > 0 ? 'left_swipe' : 'right_swipe';
      this.goTo(e.deltaX > 0 ? this.currentIndex + 1 : this.currentIndex - 1, wheelSource);
      setTimeout(() => { wheelLocked = false; }, 600);
    }, { passive: true });
  }

  // Hides (not just disables) each arrow once there's nothing left to scroll to in that
  // direction — prev at the first slide, next at the last, and both when there's only one
  // slide total. Same single-instance gallery drives both breakpoints, so this covers
  // desktop and mobile with no template changes needed.
  updateBtnState() {
    const total = this.slides.length;
    const atStart = this.currentIndex === 0;
    const atEnd = this.currentIndex === total - 1;
    if (this.prevBtn) {
      this.prevBtn.disabled = atStart;
      this.prevBtn.classList.toggle('hidden', atStart);
      this.prevBtn.classList.toggle('flex', !atStart);
    }
    if (this.nextBtn) {
      this.nextBtn.disabled = atEnd;
      this.nextBtn.classList.toggle('hidden', atEnd);
      this.nextBtn.classList.toggle('flex', !atEnd);
    }
  }

  // `source` is only passed for direct user interaction (arrow click, dot/thumb
  // click, swipe). Variant-triggered slide changes (see goToMediaId) omit it so
  // they don't get reported as a gallery interaction.
  goTo(index, source) {
    const total = this.slides.length;
    if (index < 0 || index >= total) return;
    const previousIndex = this.currentIndex;
    this.currentIndex = index;
    this.updateBtnState();
    this.track.style.willChange = 'transform';
    this.track.style.transform = `translateX(-${this.currentIndex * 100}%)`;
    this.track.addEventListener('transitionend', () => {
      this.track.style.willChange = 'auto';
    }, { once: true });
    this.dots.forEach((dot, i) => {
      dot.classList.toggle('main-pdp__gallery-dot--active', i === this.currentIndex);
    });
    this.thumbs.forEach((thumb, i) => {
      thumb.classList.toggle('pdp-gallery-thumbs__btn--active', i === this.currentIndex);
    });
    const activeThumb = this.thumbs[this.currentIndex];
    if (activeThumb && this.thumbsContainer) {
      activeThumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
    if (source && this.currentIndex !== previousIndex) {
      this.trackImageInteraction(source, previousIndex, this.currentIndex);
    }
  }

  trackImageInteraction(ctaText, previousIndex, currentIndex) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'pdp_image_interaction',
      cta_text: ctaText,
      product_name: this.productName,
      current_step: String(currentIndex + 1),
      previous_step: String(previousIndex + 1),
    });
  }

  goToMediaId(mediaId) {
    const idx = this.slides.findIndex(
      (slide) => Number(slide.dataset.mediaId) === Number(mediaId)
    );
    if (idx !== -1) this.goTo(idx);
  }

  // Narrows the gallery down to the slides rendered for one variant (from the
  // custom.variant_images variant metafield — see sections/main-pdp.liquid). Falls back
  // to the default product.media slides (the ones with no data-variant-id at all) when
  // the variant has no metafield images, so products that never fill in that metafield
  // keep working exactly as before.
  filterByVariant(variantId) {
    const id = String(variantId);
    const belongsToVariant = (el) => el.dataset.variantId === id;
    const isDefault = (el) => !el.dataset.variantId;
    const filterActive = this.allSlides.some(belongsToVariant);
    const matches = filterActive ? belongsToVariant : isDefault;

    this.allSlides.forEach((slide) => slide.classList.toggle('hidden', !matches(slide)));
    this.allThumbs.forEach((thumb) => thumb.classList.toggle('hidden', !matches(thumb)));
    this.allDots.forEach((dot) => dot.classList.toggle('hidden', !matches(dot)));

    this.slides = this.allSlides.filter(matches);
    this.thumbs = this.allThumbs.filter(matches);
    this.dots = this.allDots.filter(matches);

    // Snap to the first slide of the new subset without animating through the gap left
    // by the slides that just got hidden.
    this.currentIndex = 0;
    this.track.style.transition = 'none';
    this.goTo(0);
    void this.track.offsetHeight;
    this.track.style.transition = '';
  }
}

document.addEventListener('DOMContentLoaded', () => {
  if (typeof subscribe === 'undefined' || typeof PUB_SUB_EVENTS === 'undefined') return;

  const pdpSection = document.querySelector('.main-pdp[data-product-id]');
  if (!pdpSection) return;

  // Groups a whole-number amount with commas every 3 digits (1884 → "1,884").
  // Prices are computed client-side (MRP − discount) with the ₹ symbol in a
  // separate span, so the Liquid `money` filter can't be applied on variant change.
  const formatThousands = (amount) => Number(amount).toLocaleString('en-US');

  const productId = pdpSection.dataset.productId;

  const galleryEl = pdpSection.querySelector('[data-pdp-gallery]');
  const gallery = galleryEl ? new MainPdpGallery(galleryEl) : null;

  // Offer cards are identical whether rendered on the desktop or mobile
  // instance of the snippet, so either one's embedded JSON works for both.
  const offersDataEl = document.querySelector('[data-pdp-offers-by-variant]');
  const offersByVariant = (() => {
    try { return JSON.parse(offersDataEl?.textContent || '{}'); }
    catch (_) { return {}; }
  })();

  const buildOfferCard = (offer) => {
    const card = document.createElement('div');
    card.className = 'main-pdp__offer-card';

    const header = document.createElement('div');
    header.className = 'main-pdp__offer-header';
    if (offer.title) {
      const title = document.createElement('p');
      title.className = 'main-pdp__offer-title';
      title.textContent = offer.title;
      header.appendChild(title);
    }
    if (offer.description) {
      const desc = document.createElement('p');
      desc.className = 'main-pdp__offer-desc';
      desc.textContent = offer.description;
      header.appendChild(desc);
    }
    card.appendChild(header);

    if (offer.coupon) {
      const codeRow = document.createElement('div');
      codeRow.className = 'main-pdp__offer-code-row';

      const label = document.createElement('span');
      label.className = 'main-pdp__offer-code-label';
      label.append('Use code: ');
      const code = document.createElement('span');
      code.className = 'main-pdp__offer-code';
      code.textContent = offer.coupon;
      label.appendChild(code);
      codeRow.appendChild(label);

      const copyBtn = document.createElement('button');
      copyBtn.className = 'main-pdp__offer-copy';
      copyBtn.type = 'button';
      copyBtn.dataset.pdpCopy = offer.coupon;
      copyBtn.setAttribute('aria-label', `Copy code ${offer.coupon}`);
      copyBtn.textContent = 'Copy';
      codeRow.appendChild(copyBtn);

      card.appendChild(codeRow);
    } else {
      const noCode = document.createElement('span');
      noCode.className = 'main-pdp__offer-no-code';
      noCode.textContent = 'No code required';
      card.appendChild(noCode);
    }

    return card;
  };

  const renderOffersForVariant = (variantId) => {
    const offers = offersByVariant[variantId] ?? [];
    document.querySelectorAll('[data-pdp-offers-track]').forEach((track) => {
      track.innerHTML = '';
      offers.forEach((offer) => track.appendChild(buildOfferCard(offer)));
    });
  };

  // Delegated on document so it covers both the desktop offers (inside .main-pdp)
  // and the separate mobile offers section (.pdp-mobile-offers), which lives
  // outside .main-pdp and would otherwise get no copy handler on mobile.
  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-pdp-copy]');
    if (!btn) return;

    const code = btn.dataset.pdpCopy;
    const original = btn.textContent;
    const showCopied = () => {
      btn.textContent = 'Copied!';
      setTimeout(() => { btn.textContent = original; }, 2000);
    };

    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(code).then(showCopied).catch(() => { btn.textContent = original; });
    } else {
      showCopied();
    }
  });

  // Width-locking for color-section is handled by CSS min-width: fit-content.

  // All-shades toggle
  pdpSection.querySelectorAll('[data-all-shades-toggle]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const group = btn.closest('[data-option-group]');
      const panel = group.querySelector('[data-all-shades-panel]');

      if (panel) {
        const row = group.querySelector('[data-swatch-row]');
        const isOpen = panel.classList.contains('is-open');
        panel.classList.toggle('is-open', !isOpen);
        if (row) row.classList.toggle('is-hidden', !isOpen);
        btn.setAttribute('aria-expanded', String(!isOpen));
        btn.textContent = !isOpen ? 'Hide shades <' : 'All shades >';
      } else {
        const row = group.querySelector('[data-swatch-row]');
        if (!row) return;
        const expanded = row.classList.toggle('is-expanded');
        btn.setAttribute('aria-expanded', String(expanded));
        btn.textContent = expanded ? 'Hide shades <' : 'All shades >';
      }
    });
  });


  // Quality tags popup
  const qtOverlay = pdpSection.querySelector('[data-pdp-qt-overlay]');
  if (qtOverlay) {
    const qtTags = pdpSection.querySelectorAll('[data-pdp-quality-tag]');
    const qtTabs = qtOverlay.querySelectorAll('[data-pdp-qt-tab]');
    const qtPanels = qtOverlay.querySelectorAll('[data-pdp-qt-panel]');
    const qtClose = qtOverlay.querySelector('[data-pdp-qt-close]');

    const activateQtTab = (index) => {
      qtTabs.forEach((tab, i) => {
        tab.classList.toggle('main-pdp-quality-tags-popup__tab--active', i === index);
      });
      qtPanels.forEach((panel, i) => {
        panel.classList.toggle('main-pdp-quality-tags-popup__panel--active', i === index);
      });
    };

    const openQtPopup = (index) => {
      qtOverlay.hidden = false;
      document.body.classList.add('qt-popup-open');
      activateQtTab(index);
    };

    const closeQtPopup = () => {
      qtOverlay.hidden = true;
      document.body.classList.remove('qt-popup-open');
    };

    qtTags.forEach((tag) => {
      tag.addEventListener('click', () => openQtPopup(Number(tag.dataset.pdpQualityTag)));
    });

    qtTabs.forEach((tab) => {
      tab.addEventListener('click', () => activateQtTab(Number(tab.dataset.pdpQtTab)));
    });

    qtClose?.addEventListener('click', closeQtPopup);

    qtOverlay.addEventListener('click', (e) => {
      if (e.target === qtOverlay) closeQtPopup();
    });
  }

  subscribe(PUB_SUB_EVENTS.variantChange, (data) => {
    if (data.productId !== productId) return;

    const variant = data.variant;
    if (!variant) return;

    const priceIntEl = pdpSection.querySelector('[data-pdp-price-integer]');
    const comparePriceEl = pdpSection.querySelector('[data-pdp-compare-price]');
    const comparePriceIntEl = pdpSection.querySelector('[data-pdp-compare-price-integer]');
    const discountEl = pdpSection.querySelector('[data-pdp-discount]');
    const saveEl = pdpSection.querySelector('[data-pdp-save]');

    const mrpRupees = Math.round(variant.price / 100);

    if (saveEl) {
      // amount / BAU mode: current price = MRP − flat discount, MRP struck through.
      // The discounted amount is rounded to the nearest rupee and the save amount is
      // derived from it, so MRP − save always equals the displayed price.
      const savePct = parseInt(saveEl.dataset.pdpSavePct, 10) || 15;
      const discountedRupees = Math.round((variant.price * (100 - savePct)) / 10000);
      const saveAmount = mrpRupees - discountedRupees;

      if (priceIntEl) priceIntEl.textContent = formatThousands(discountedRupees);

      const saveAmountEl = saveEl.querySelector('[data-pdp-save-amount]');
      if (saveAmountEl) saveAmountEl.textContent = formatThousands(saveAmount);
      saveEl.hidden = saveAmount <= 0;

      if (comparePriceEl) {
        if (comparePriceIntEl) comparePriceIntEl.textContent = formatThousands(mrpRupees);
        comparePriceEl.hidden = saveAmount <= 0;
      }
    } else {
      // sale mode: current price = variant price, compare_at_price struck through
      // — unless the variant has its own flat discount %, which takes priority
      // and turns the variant price itself into the struck-through compare price.
      const vdDiscountPaise = Number(variant.vd_discount_paise || 0);

      if (vdDiscountPaise > 0) {
        const finalPricePaise = Math.max(variant.price - vdDiscountPaise, 0);
        if (priceIntEl) priceIntEl.textContent = formatThousands(Math.round(finalPricePaise / 100));

        if (comparePriceEl) {
          if (comparePriceIntEl) comparePriceIntEl.textContent = formatThousands(mrpRupees);
          comparePriceEl.hidden = false;
        }
        if (discountEl) {
          const discountPct = Math.round((vdDiscountPaise / variant.price) * 100);
          discountEl.textContent = '('+ discountPct + '% OFF)';
          discountEl.hidden = false;
        }
      } else {
        if (priceIntEl) priceIntEl.textContent = formatThousands(mrpRupees);

        if (comparePriceEl) {
          if (variant.compare_at_price > variant.price) {
            if (comparePriceIntEl) comparePriceIntEl.textContent = formatThousands(Math.round(variant.compare_at_price / 100));
            comparePriceEl.hidden = false;
            if (discountEl) {
              const discount = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
              discountEl.textContent = '('+ discount + '% OFF)';
              discountEl.hidden = false;
            }
          } else {
            comparePriceEl.hidden = true;
            if (discountEl) discountEl.hidden = true;
          }
        }
      }
    }

    const url = new URL(window.location);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);

    // Update active shade name for color options
    pdpSection.querySelectorAll('[data-shade-name]').forEach((el) => {
      const idx = Number(el.dataset.optionIndex);
      if (variant.options && variant.options[idx] != null) {
        el.textContent = variant.options[idx];
      }
    });

    if (gallery) {
      gallery.filterByVariant(variant.id);
      if (variant.featured_media_id) gallery.goToMediaId(variant.featured_media_id);
    }

    renderOffersForVariant(variant.id);
  });
});

document.addEventListener('DOMContentLoaded', () => {
  const stickyAtc = document.querySelector('[data-sticky-atc]');
  if (!stickyAtc) return;

  // Groups a whole-number amount with commas every 3 digits (1884 → "1,884").
  const formatThousands = (amount) => Number(amount).toLocaleString('en-US');

  const stickyQtyDec = stickyAtc.querySelector('[data-sticky-qty-dec]');
  const stickyQtyInc = stickyAtc.querySelector('[data-sticky-qty-inc]');
  const stickyQtyDisplay = stickyAtc.querySelector('[data-sticky-qty-display]');
  const stickyQtyInput = stickyAtc.querySelector('[data-sticky-qty-input]');

  // capped-product-bogo (hard cap) caps the sticky bar at 1 unit — data-sticky-max-qty
  // is only rendered onto the wrapper when the product tag or the selected variant's
  // Metaobject rule (custom.bogo_variant_rule, field capped_at_1) applies it.
  // data-sticky-tag-capped-bogo is the product-tag-driven floor (applies to every
  // variant) — re-evaluated below on each variant switch alongside the variant's own
  // bogo_capped_at_1 flag.
  const stickyTagCappedBogo = stickyAtc.hasAttribute('data-sticky-tag-capped-bogo');
  let stickyMaxQty = parseInt(stickyAtc.dataset.stickyMaxQty || '', 10) || Infinity;

  let stickyQty = 1;

  const updateStickyQty = (newQty) => {
    if (newQty < 1 || newQty > stickyMaxQty) return;
    stickyQty = newQty;
    if (stickyQtyDisplay) stickyQtyDisplay.textContent = stickyQty;
    if (stickyQtyInput) stickyQtyInput.value = stickyQty;
    if (stickyQtyDec) stickyQtyDec.disabled = stickyQty <= 1;
    if (stickyQtyInc) stickyQtyInc.disabled = stickyQty >= stickyMaxQty;
  };

  stickyQtyDec?.addEventListener('click', () => updateStickyQty(stickyQty - 1));
  stickyQtyInc?.addEventListener('click', () => updateStickyQty(stickyQty + 1));

  if (typeof subscribe === 'undefined' || typeof PUB_SUB_EVENTS === 'undefined') return;

  const stickyProductId = stickyAtc.dataset.productId;
  const stickyOptionIdx = parseInt(stickyAtc.dataset.stickyOptionIdx || '0', 10);
  const stickySavePct = stickyAtc.dataset.stickySavePct ? parseInt(stickyAtc.dataset.stickySavePct, 10) : null;
  const stickyImgMapEl = stickyAtc.querySelector('[data-sticky-variant-images]');
  const stickyVariantImages = (() => {
    try { return JSON.parse(stickyImgMapEl?.textContent || '{}'); }
    catch (_) { return {}; }
  })();

  subscribe(PUB_SUB_EVENTS.variantChange, (data) => {
    if (String(data.productId) !== String(stickyProductId)) return;

    const variant = data.variant;
    if (!variant) return;

    // capped-product-bogo (hard cap) can also be set per-variant via a Metaobject
    // rule — re-evaluate the cap on every switch and clamp the current quantity down
    // if it now exceeds it. stickyTagCappedBogo (product-tag-driven) is a floor: it
    // never gets un-capped here.
    stickyMaxQty = (stickyTagCappedBogo || variant.bogo_capped_at_1) ? 1 : Infinity;
    updateStickyQty(Math.min(stickyQty, stickyMaxQty));

    const stickyVariantName = stickyAtc.querySelector('[data-sticky-variant-name]');
    if (stickyVariantName) {
      const optionVal = variant.options?.[stickyOptionIdx];
      stickyVariantName.textContent = optionVal ? ` (${optionVal})` : '';
    }

    const stickyPrice = stickyAtc.querySelector('[data-sticky-price]');
    if (stickyPrice) {
      const mrpInt = Math.round(variant.price / 100);

      const vdDiscountPaise = Number(variant.vd_discount_paise || 0);

      if (stickySavePct != null) {
        // amount / BAU mode: sticky price = MRP − flat discount, rounded to the nearest
        // rupee so it matches the main price block (see [data-pdp-price-integer] above).
        const discountedRupees = Math.round((variant.price * (100 - stickySavePct)) / 10000);
        stickyPrice.textContent = formatThousands(discountedRupees);
      } else if (vdDiscountPaise > 0) {
        // product mode with a variant-level discount %: sticky price = variant price − discount
        const finalPricePaise = Math.max(variant.price - vdDiscountPaise, 0);
        stickyPrice.textContent = formatThousands(Math.round(finalPricePaise / 100));
      } else {
        stickyPrice.textContent = formatThousands(mrpInt);
      }
    }

    const stickyImgWrap = stickyAtc.querySelector('[data-sticky-img-wrap]');
    if (stickyImgWrap) {
      const newImgUrl = stickyVariantImages[variant.id];
      if (newImgUrl) {
        const img = stickyImgWrap.querySelector('img');
        const sources = stickyImgWrap.querySelectorAll('source');
        if (img) img.src = newImgUrl;
        sources.forEach((source) => { source.srcset = newImgUrl; });
      }
    }
  });
});

document.addEventListener('DOMContentLoaded', () => {
  const infoCol = document.querySelector('.main-pdp__info');
  if (infoCol) {
    function relocateFlashChat() {
      const chat = document.querySelector('.shop-ai-chat-container');
      if (!chat || chat.classList.contains('pdp-flash-chat-relocated')) return;

      const atcBlock = infoCol.querySelector('.pdp-block--add-to-cart');
      if (atcBlock) {
        atcBlock.insertAdjacentElement('afterend', chat);
      } else {
        infoCol.appendChild(chat);
      }
      chat.classList.add('pdp-flash-chat-relocated');
    }

    relocateFlashChat();

    function patchChatA11y() {
      const sendBtn = document.querySelector('.shop-ai-chat-send');
      if (sendBtn && !sendBtn.getAttribute('aria-label')) {
        sendBtn.setAttribute('aria-label', 'Send message');
      }
      const triggerBtn = document.querySelector('.shop-ai-chat-trigger-button');
      if (triggerBtn && !triggerBtn.getAttribute('aria-label')) {
        triggerBtn.setAttribute('aria-label', 'Open chat');
      }
      document.querySelectorAll('.shop-ai-compact-card-title').forEach((el) => {
        if (!el.getAttribute('role')) el.setAttribute('role', 'none');
      });
    }

    patchChatA11y();

    const chatObserver = new MutationObserver(() => {
      relocateFlashChat();
      patchChatA11y();
    });
    chatObserver.observe(document.body, { childList: true, subtree: true });
    setTimeout(() => chatObserver.disconnect(), 15000);
  }
});

document.addEventListener('DOMContentLoaded', () => {
  const strip = document.querySelector('[data-sticky-strip]');
  if (!strip) return;

  const rows = Array.from(strip.querySelectorAll('[data-strip-row]'));
  if (rows.length === 0) return;

  let currentIdx = 0;

  let tickerStarted = false;

  function startTicker() {
    if (tickerStarted || rows.length <= 1) return;
    tickerStarted = true;
    rows[0].classList.add('is-active');
    setInterval(() => {
      const prevIdx = currentIdx;
      currentIdx = (currentIdx + 1) % rows.length;
      rows[prevIdx].classList.add('is-exiting');
      rows[prevIdx].classList.remove('is-active');
      rows[currentIdx].classList.add('is-active');
      setTimeout(() => rows[prevIdx].classList.remove('is-exiting'), 400);
    }, 3000);
  }

  window.addEventListener('scroll', () => {
    if (window.scrollY >= 500) {
      strip.classList.add('is-visible');
      if (!tickerStarted) {
        rows[0].classList.add('is-active');
        startTicker();
      }
    } else {
      strip.classList.remove('is-visible');
    }
  }, { passive: true });
});
